/**
 * The compass: where a pivot sits is what it means (#594).
 *
 * Angle from the scenario mix; radius from how evenly the scenarios share
 * the pivot (entropy — not vector length, or two opposite walls would cancel
 * to the centre and read as "all four"); size from convergence. No force
 * simulation: the same data gives the same map on every tick, and a
 * collision moves a node along its radius only, never its angle.
 *
 * Pure functions, no React, so the layout can be tested and reused.
 */
import { SCENARIO_COLORS } from '../../config/brandColors';

export const SIZE = 1000;
export const CENTRE = SIZE / 2;
export const R_OUTER = 400;
export const R_INNER = 165; // the inner ring is the question's

// The compass is the room: scenarios in wall order, A–D clockwise from north.
const WALLS = [
  { angle: -90, wall: 'A · Pohjoinen' },
  { angle: 0, wall: 'B · Itä' },
  { angle: 90, wall: 'C · Etelä' },
  { angle: 180, wall: 'D · Länsi' },
];
const FALLBACK_COLORS = ['#FFC107', '#F44336', '#4CAF50', '#2196F3', '#9C27B0', '#FF9800'];

export const rad = (deg) => (deg * Math.PI) / 180;

/** Scenarios as the scenarios endpoint returns them → { [id]: { title, angle, wall, color } }. */
export function compassAxes(scenarios = []) {
  const ordered = [...scenarios].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));
  const n = ordered.length || 1;
  const axes = {};
  ordered.forEach((s, i) => {
    const std = n <= WALLS.length ? WALLS[i] : null;
    axes[s.id] = {
      id: s.id,
      title: s.title,
      angle: std ? std.angle : -90 + (360 / n) * i,
      wall: std ? std.wall : '',
      color: s.color || SCENARIO_COLORS[s.title] || FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    };
  });
  return axes;
}

/** Members per scenario for one candidate. */
export function scenarioShares(candidate, nodesById) {
  const shares = {};
  for (const id of candidate.memberIds || []) {
    const sid = nodesById[id]?.scenarioId;
    if (sid) shares[sid] = (shares[sid] || 0) + 1;
  }
  return shares;
}

/** @returns {Array<{ c, shares, ang, r, size, x, y }>} one per candidate, same order. */
export function layoutPivots(candidates, nodesById, axes) {
  const maxConv = Math.max(1, ...candidates.map((c) => c.convergence?.value || 0));
  const axisCount = Math.max(2, Object.keys(axes).length);
  const placed = candidates.map((c) => {
    const shares = scenarioShares(c, nodesById);
    const entries = Object.entries(shares).filter(([sid]) => axes[sid]);
    const total = entries.reduce((a, [, n]) => a + n, 0) || 1;
    let x = 0; let y = 0; let H = 0; let top = null;
    for (const [sid, n] of entries) {
      const f = n / total; const a = rad(axes[sid].angle);
      x += f * Math.cos(a); y += f * Math.sin(a); H -= f * Math.log2(f);
      if (!top || n > top[1]) top = [sid, n];
    }
    const even = H / Math.log2(axisCount);               // 0 = one scenario, 1 = all evenly
    const ang = !top ? rad(-90) : Math.hypot(x, y) < 0.05 ? rad(axes[top[0]].angle) : Math.atan2(y, x);
    const r = R_INNER + (R_OUTER - R_INNER) * (1 - Math.min(1, even));
    const size = 20 + 34 * Math.sqrt((c.convergence?.value || 0) / maxConv);
    return { c, shares, ang, r, size, x: 0, y: 0 };
  });
  const place = (n) => { n.x = CENTRE + n.r * Math.cos(n.ang); n.y = CENTRE + n.r * Math.sin(n.ang); };
  placed.forEach(place);
  // Deterministic de-overlap: of two colliding nodes the inner moves inward
  // and the outer outward, along the radius, so what the position says holds.
  for (let it = 0; it < 80; it += 1) {
    for (let i = 0; i < placed.length; i += 1) {
      for (let j = i + 1; j < placed.length; j += 1) {
        const a = placed[i]; const b = placed[j];
        const d = Math.hypot(b.x - a.x, b.y - a.y); const min = a.size + b.size + 36;
        if (d >= min) continue;
        const push = (min - d) / 2;
        const [inner, outer] = a.r <= b.r ? [a, b] : [b, a];
        inner.r = Math.max(R_INNER, inner.r - push * 0.7);
        outer.r = Math.min(R_OUTER, outer.r + push * 0.7);
        place(a); place(b);
      }
    }
  }
  return placed;
}

/**
 * Real arcs only: an accepted loop that runs through two pivots, a typed
 * cross-link between their members, a confirmed sama asia. No similarity
 * lines. Read from the map, so the pair is always known (#594). One entry
 * per pair, carrying everything that joins it, so the picture gets one arc
 * and one label per pair.
 *
 * @returns {Array<{ a, b, loops: object[], cross: string[], same: number, kind }>}
 */
export function pivotEdges(placed, loops = [], connections = []) {
  const pivotOf = new Map();
  for (const n of placed) for (const id of n.c.memberIds || []) pivotOf.set(id, n.c.id);
  const pairs = new Map();
  const pair = (a, b) => {
    if (!a || !b || a === b) return null;
    const [p, q] = a < b ? [a, b] : [b, a];
    const key = `${p}|${q}`;
    if (!pairs.has(key)) pairs.set(key, { a: p, b: q, loops: [], cross: [], same: 0 });
    return pairs.get(key);
  };
  for (const loop of loops) {
    if (loop.accepted === false) continue;
    const through = [...new Set((loop.nodeIds || []).map((id) => pivotOf.get(id)).filter(Boolean))];
    for (let i = 0; i < through.length; i += 1) {
      for (let j = i + 1; j < through.length; j += 1) {
        const e = pair(through[i], through[j]);
        if (e && !e.loops.some((l) => l.id === loop.id)) e.loops.push(loop);
      }
    }
  }
  for (const c of connections) {
    const e = pair(pivotOf.get(c.parentId), pivotOf.get(c.childId));
    if (!e) continue;
    if (c.type === 'is_same' || c.type === 'multi-parent') e.same += 1;
    else e.cross.push(c.type);
  }
  return [...pairs.values()].map((e) => ({ ...e, kind: e.loops.length ? 'loop' : e.cross.length ? 'cross' : 'same' }));
}

/** Accepted loops through any member of each pivot; the run's own loop ids as a fallback. */
export function loopsByPivot(placed, loops = []) {
  const byId = new Map(loops.map((l) => [l.id, l]));
  const out = {};
  for (const n of placed) {
    const members = new Set(n.c.memberIds || []);
    const hit = loops.filter((l) => l.accepted !== false && (l.nodeIds || []).some((id) => members.has(id)));
    const seen = new Set(hit.map((l) => l.id));
    for (const id of n.c.leverage?.facts?.loopIds || []) {
      if (!seen.has(id) && byId.has(id)) { hit.push(byId.get(id)); seen.add(id); }
    }
    out[n.c.id] = hit;
  }
  return out;
}

/**
 * Jäsenet: every member as a dot beside its pivot on the side of the
 * scenario it came from, and the real chains (parent → child) between
 * members. This is what "connected pivots" means when the edges are the
 * map's own, not similarity.
 */
export function layoutMembers(placed, nodesById, axes) {
  const pos = {};
  for (const n of placed) {
    const groups = {};
    for (const id of n.c.memberIds || []) {
      const node = nodesById[id];
      if (!node || !axes[node.scenarioId]) continue;
      (groups[node.scenarioId] ||= []).push(node);
    }
    for (const [sid, list] of Object.entries(groups)) {
      const base = rad(axes[sid].angle); const perRow = 7;
      list.forEach((node, i) => {
        const row = Math.floor(i / perRow); const k = i % perRow;
        const cnt = Math.min(list.length - row * perRow, perRow);
        const spread = rad(Math.min(70, 12 * cnt));
        const a = base + (cnt > 1 ? -spread / 2 + (spread * k) / (cnt - 1) : 0);
        const r = n.size + 16 + row * 11;
        pos[node.id] = { x: n.x + r * Math.cos(a), y: n.y + r * Math.sin(a), node, pivotId: n.c.id };
      });
    }
  }
  const chains = [];
  for (const p of Object.values(pos)) {
    const parent = p.node.parentId ? pos[p.node.parentId] : null;
    if (parent) chains.push({ from: parent, to: p, cross: parent.pivotId !== p.pivotId });
  }
  return { pos, chains };
}

/** Greedy word wrap for SVG labels. */
export function wrapWords(text = '', max = 20, lines = 3) {
  const out = []; let cur = '';
  for (const w of String(text).split(/\s+/).filter(Boolean)) {
    if ((`${cur} ${w}`).trim().length > max && cur) { out.push(cur.trim()); cur = w; } else cur = `${cur} ${w}`;
  }
  if (cur.trim()) out.push(cur.trim());
  return out.slice(0, lines);
}
